import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/helpers/classNames"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
>

/**
 * Changes from the original:
 * bg-popover text-popover-foreground => bg-neutral-600 text-neutral-100
 */
function TooltipContent({
  className,
  ref,
  sideOffset = 4,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof TooltipPrimitive.Content>>
} & TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn([
          "bg-neutral-600 text-neutral-100", // our styles
          `
            z-50 animate-in overflow-hidden rounded-md border px-3 py-1.5
            text-sm shadow-md fade-in-0 zoom-in-95
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0
            data-[state=closed]:zoom-out-95
          `,
          className,
        ])}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
}

function TooltipWrapper({
  children,
  contentProps,
  tooltip,
}: React.PropsWithChildren<{
  contentProps?: TooltipContentProps
  tooltip?: React.ReactNode
}>) {
  return tooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-96 whitespace-normal" {...contentProps}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  ) : (
    <>{children}</>
  )
}

export {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipWrapper,
}
